/**
 * HUD — In-game heads-up display overlay using Babylon.js GUI.
 *
 * Displays:
 *   1. Active character indicator (solo mode only)
 *   2. Ability cooldown indicators with radial fill
 *   3. Puzzle progress tracker
 *   4. Contextual interaction prompts
 *   5. Toast notifications (slide in, auto-dismiss)
 *
 * Gothic steampunk aesthetic: dark semi-transparent panels with brass borders.
 * Minimal and unobtrusive — the world's visual richness should dominate.
 */

import { Scene } from "@babylonjs/core/scene";
import { AdvancedDynamicTexture } from "@babylonjs/gui/2D/advancedDynamicTexture";
import { Rectangle } from "@babylonjs/gui/2D/controls/rectangle";
import { TextBlock } from "@babylonjs/gui/2D/controls/textBlock";
import { Ellipse } from "@babylonjs/gui/2D/controls/ellipse";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel";
import { Control } from "@babylonjs/gui/2D/controls/control";
import { Image } from "@babylonjs/gui/2D/controls/image";
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";
import { COLORS, ENERGY, BISCUITS } from "../config/GameConfig";
import { configureResponsiveFullscreenUI } from "./guiScaling";
import { applyWrappedTextLayout } from "./wrappedText";
import { formatControlText, detectControlGlyphScheme } from "./ControlGlyphs";
import { resolveCue, resolveControlsCue, type CueDef, type CueSegment, type CueCat } from "./tutorialCues";
import { Vector3, Matrix } from "@babylonjs/core/Maths/math.vector";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { AccessibilitySystem } from "../systems/AccessibilitySystem";
import { progressToSweepAngle, crossedReady, cooldownStateForSlot, buildSweepDataUri } from "./cooldown/CooldownMath";
import { blockedToastFor } from "../systems/audio/SfxEventMap";
import { placeCueBelowTopHud } from "./cuePlacement";
import type { ScreenRect } from "./cuePlacement";
import { placePanelAvoidingRects } from "./panelPlacement";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";

/**
 * HUD-specific event keys (also registered in config/events.ts as GameEvents.HUD_*).
 * Exported here for convenience when emitting from entity proximity checks.
 */
export const HUDEvents = {
  /** Show an interaction prompt. Payload: { text: string } */
  SHOW_INTERACTION_PROMPT: GameEvents.HUD_SHOW_PROMPT,
  /** Hide the current interaction prompt. */
  HIDE_INTERACTION_PROMPT: GameEvents.HUD_HIDE_PROMPT,
  /** Show a toast notification. Payload: { message: string, color?: string } */
  SHOW_TOAST: GameEvents.HUD_TOAST,
} as const;

// ----- Types -----

interface AbilityCooldownState {
  id: string;
  name: string;
  cooldownRemaining: number;
  cooldownDuration: number;
  isActive: boolean;
}

interface ToastEntry {
  container: Rectangle;
  startTime: number;
  fadingOut: boolean;
  message: string;
}

// ----- Constants -----

const FONT_FAMILY = "'Segoe UI', 'Helvetica Neue', Arial, sans-serif";
// Panel fill is a near-opaque near-black, lightly tinted toward the
// gothic-purple palette. Earlier alpha 0.88 sampled as "barely
// visible" in the 2026-04-18 visual review: the warm Victorian walls
// on L0 (brown/amber) bled through enough that the top-left energy
// panel read as a slight dim patch rather than a UI element. 0.94 is
// still technically translucent (so the panel doesn't punch out of
// the brass border) but closer to the effective "solid card" feel the
// HUD needs against bright backdrops.
const PANEL_BG = "rgba(10, 6, 18, 0.94)";
const PANEL_BORDER = COLORS.BRASS;
const MINNA_COLOR = "#00ff88";
const BASIL_COLOR = "#ff8844";
const TEXT_COLOR = "#f5eedb";

interface CueSlot {
  def: CueDef | "controls" | null;
  anchor: Vector3 | null;
  panel: Rectangle;
  row: StackPanel;
  /** -1/1 while the anchor is off the left/right edge (adds an arrow). */
  side: -1 | 0 | 1;
  segments?: CueSegment[];
}

function cueCatName(cat: CueCat): string {
  return cat === "minna" ? "Minna" : cat === "basil" ? "Basil" : "Both cats";
}

function cueCatColor(cat: CueCat): string {
  return cat === "minna" ? MINNA_COLOR : cat === "basil" ? BASIL_COLOR : COLORS.BRASS;
}
const DIM_TEXT = "#c5b089";
const TOAST_DURATION_MS = 3000;
const TOAST_FADE_MS = 400;
const TOAST_DEDUPE_WINDOW_MS = 1200;
const MAX_ACTIVE_TOASTS = 4;
const ABILITY_SLOT_SIZE = 48;
const ABILITY_RING_SIZE = 31;
const ABILITY_FILL_READY_SIZE = 25;
const ENERGY_PANEL_FULL_WIDTH = 220;
const ENERGY_PANEL_FULL_HEIGHT = 94;
const HARNESS_CHIP_WIDTH = 176;
const HARNESS_CHIP_HEIGHT = 30;
const ABILITY_RAIL_TOP_WITH_ENERGY = 166;
const ABILITY_RAIL_TOP_WITH_HARNESS_CHIP = 96;

/** Short pill labels for the ability HUD. Keep <= 6 chars so compact pills stay readable. */
const ABILITY_SHORT_LABELS: Record<string, string> = {
  auraSight: "Aura",
  shadowSlip: "Shadow",
  calmingResonance: "Purr",
  turnTableTouch: "Spin",
  pneumaticAnchor: "Anchor",
  overclockedLeap: "Leap",
  resonantDrop: "Drop",
  chronosBubble: "Bubble",
};

/** Canonical ability display order. Unearned/reserved abilities stay out of the play HUD. */
const ABILITY_SLOT_ORDER: string[] = [
  "auraSight",
  "shadowSlip",
  "calmingResonance",
  "turnTableTouch",
  "pneumaticAnchor",
  "overclockedLeap",
  "resonantDrop",
  "chronosBubble",
  // Remaining slots reserved for future abilities. The removed
  // `groundingShockwaves` slot was a code-invented drift retired during
  // the 2026-04-14 canon reconciliation — Canon p.25 names "Kitty
  // Grounding shockwaves" as a synonym for Resonant Drop.
];

export class HUD {
  private scene: Scene;
  private readonly atmospheric: boolean;
  private readonly avoidActors: boolean;
  private readonly cinematicActive: (()=>boolean)|undefined;
  private actorRects: ScreenRect[] = [];
  private headerRect: ScreenRect | null = null;
  private abilityRailRect: ScreenRect | null = null;
  private readonly panelBg: string;
  private readonly panelBorder: string;
  private readonly fontFamily: string;
  private guiTexture: AdvancedDynamicTexture;
  private disposeGuiScaling: (() => void) | null = null;

  // Active character indicator
  private characterPanel: Rectangle | null = null;
  private characterLabel: TextBlock | null = null;
  private characterDot: Ellipse | null = null;

  // Ability cooldowns
  private abilitiesPanel: StackPanel | null = null;
  private abilitySlots: Map<string, { layout: Control; bg: Rectangle; fill: Ellipse; label: TextBlock; ring: Ellipse; sweepImage: Image; prevAngleBucket: number; canonicalRingColor: string; flashInProgress: boolean }> = new Map();
  private abilityCooldowns: Map<string, AbilityCooldownState> = new Map();
  private prevCooldownRemaining: Map<string, number> = new Map();
  private abilityStateProvider: (() => Array<{ id: string; cooldownRemaining: number; cooldownDuration: number; isActive: boolean }>) | null = null;

  // Puzzle progress
  private puzzlePanel: Rectangle | null = null;
  private puzzleLabel: TextBlock | null = null;
  private puzzleVisible = false;

  // Always-on objective line — NOT gated by hintsEnabled.
  // Vertical band layout (top-centre stack, no intersections):
  //   Puzzle counter : top 16px, height 40px → band [16, 56]
  //   Objective line : top 64px, height 28px → band [64, 92]  (8px gap below puzzle)
  //   Prompt panel   : BOTTOM-anchored at -120px               (no overlap with top stack)
  private objectivePanel: Rectangle | null = null;
  private objectiveLabel: TextBlock | null = null;

  // Tutorial cue chips (ui/tutorialCues.ts). "objective" is pinned above
  // a world point through cueProxy; "controls" sits at the bottom edge.
  private cueSlots: Record<"objective" | "controls", CueSlot> | null = null;
  private cueProxy: TransformNode | null = null;
  private cutsceneActive = false;

  // Interaction prompt
  private promptPanel: Rectangle | null = null;
  private promptLabel: TextBlock | null = null;
  private promptContentContainer: StackPanel | null = null;
  private controllerActive = false;

  // Toast notifications
  private toastContainer: StackPanel | null = null;
  private activeToasts: ToastEntry[] = [];

  // Biscuits kneading overlay — shown only while a Making-Biscuits
  // session is active. Two paw pads animate on alternating beats; a
  // trance-depth bar fills from 0 → 100.
  private biscuitsPanel: Rectangle | null = null;
  private biscuitsLeftPad: Ellipse | null = null;
  private biscuitsRightPad: Ellipse | null = null;
  private biscuitsDepthBg: Rectangle | null = null;
  private biscuitsDepthFill: Rectangle | null = null;
  private biscuitsTitle: TextBlock | null = null;
  private biscuitsHint: TextBlock | null = null;
  private biscuitsActive = false;
  private biscuitsDepth = 0; // 0..100
  private biscuitsLastPaw: "left" | "right" | null = null;
  private biscuitsLastPawFlashAt = 0;
  private biscuitsLastPawQuality: "good" | "soft" = "good";

  // Energy bars
  private energyPanel: Rectangle | null = null;
  private minnaEnergyBg: Rectangle | null = null;
  private minnaEnergyFill: Rectangle | null = null;
  private minnaEnergyLabel: TextBlock | null = null;
  private basilEnergyBg: Rectangle | null = null;
  private basilEnergyFill: Rectangle | null = null;
  private basilEnergyLabel: TextBlock | null = null;
  private minnaEnergyFraction = 1;
  private basilEnergyFraction = 1;
  private minnaEnergyDepleted = false;
  private basilEnergyDepleted = false;
  private energyEnabled = false;
  private depletedFlashTimer = 0;

  // Harness integrity (Basil) — thin copper bar below the Basil energy bar.
  // Lives on every level because HarnessWoundSystem is always active; the
  // bar sits at low alpha while Intact (quiet), full alpha once wounded.
  private harnessBarBg: Rectangle | null = null;
  private harnessBarFill: Rectangle | null = null;
  private harnessIcon: TextBlock | null = null;
  private harnessFraction = 1;
  private harnessState: "intact" | "cracked" | "venting" | "limping" = "intact";
  private harnessHealPulseTimer = 0;

  // #15 Separation-tether indicator — thin vertical edge strips that
  // light up when PLAYER_SEPARATION_CLAMPED fires. The damped cat is
  // being pulled back by the camera tether; without a visual signal
  // the player just perceives their input as silently ignored. We
  // render a strip on the side the clamped cat is being pulled FROM
  // (i.e. the direction of the partner), with opacity driven by
  // `1 - damping`. The damping target decays if no clamp events
  // arrive for a few frames so the strip fades out once the cats
  // reunite.
  private separationLeftStrip: Rectangle | null = null;
  private separationRightStrip: Rectangle | null = null;
  /** 0 = no clamp, 1 = fully clamped (dx beyond soft-band). */
  private separationClampIntensity = 0;
  /** Which side the damped cat is being pulled FROM: "left" or "right". */
  private separationClampSide: "left" | "right" | null = null;
  /** Timestamp (ms) of most recent clamp event; used to fade out. */
  private separationClampLastEmitAt = 0;

  // State
  private activePlayer: 1 | 2 = 1;
  // Default co-op (see InputManager.ts for rationale).
  private soloMode = false;
  private currentPuzzle = 0;
  private totalPuzzles = 0;

  // Event unsubscribers
  private unsubscribers: (() => void)[] = [];

  // Puzzle-hint toggle. Default ON — the bottom-center prompt carries
  // tutorial instructions (Level 0) and narrative beats (Levels 1-2)
  // that are load-bearing for first-time players. An earlier change
  // flipped the default OFF, which silently dropped every prompt on
  // every level (confirmed via Gemini fluidity reviews 2026-04-18:
  // tutorialGate, seizedCrane, masterValve all emitted
  // `hud:showPrompt` events but no text rendered). Players who want
  // to explore without hints can still press H to hide them; the
  // preference persists via localStorage under
  // `catsOfChronos.hintsEnabled`.
  private hintsEnabled = true;
  /** Cached so toggling hints back on re-shows the current puzzle's prompt. */
  private lastPromptText: string | null = null;
  private hintsKeyHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(scene: Scene, options: { atmospheric?: boolean; avoidActors?: boolean; cinematicActive?: ()=>boolean } = {}) {
    this.atmospheric = options.atmospheric ?? false;
    this.avoidActors = options.avoidActors ?? false;
    this.cinematicActive=options.cinematicActive;
    this.cutsceneActive=this.avoidActors&&(this.cinematicActive?.()??false);
    this.panelBg = this.atmospheric ? "rgba(19, 29, 25, 0.82)" : PANEL_BG;
    this.panelBorder = this.atmospheric ? "#82775e" : PANEL_BORDER;
    this.fontFamily = this.atmospheric ? "system-ui, sans-serif" : FONT_FAMILY;
    this.scene = scene;
    this.guiTexture = AdvancedDynamicTexture.CreateFullscreenUI("hudUI", true, scene);
    if(this.avoidActors)this.guiTexture.rootContainer.isVisible=!this.cutsceneActive;
    this.disposeGuiScaling = configureResponsiveFullscreenUI(this.guiTexture, scene);

    // Restore hint preference from localStorage if available. Only an
    // explicit `"false"` overrides the ON default — a missing or
    // unreadable value leaves hints enabled so a fresh install still
    // shows the tutorial prompts.
    try {
      const stored =
        typeof window !== "undefined"
          ? window.localStorage?.getItem("catsOfChronos.hintsEnabled")
          : null;
      if (stored === "false") this.hintsEnabled = false;
    } catch {
      /* private-mode browsers may throw; default stays true */
    }
    this.hintsKeyHandler = (e: KeyboardEvent) => {
      // Only consume `h` when it's not part of a modifier combo (so
      // browser shortcuts like Cmd+Shift+H still work).
      if (e.key === "h" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        this.toggleHints();
      }
    };
    if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
      window.addEventListener("keydown", this.hintsKeyHandler);
    }

    // Start from the live input state (the mode may have been set before
    // this HUD existed); INPUT_MODE_CHANGED / PLAYER_SWITCHED keep it fresh.
    const input = (scene.metadata as { inputManager?: { soloMode?: boolean; activePlayer?: 1 | 2 } } | null)?.inputManager;
    if (input) {
      this.soloMode = input.soloMode ?? false;
      this.activePlayer = input.activePlayer ?? 1;
    }

    this.buildCharacterIndicator();
    this.buildEnergyBars();
    this.buildAbilitiesPanel();
    this.buildPuzzleProgress();
    this.buildObjectiveLine();
    this.buildInteractionPrompt();
    this.buildCues();
    this.buildToastContainer();
    this.buildBiscuitsOverlay();
    this.buildSeparationTether();
    this.subscribeToEvents();
    this.startUpdateLoop();
  }

  // ===================================================================
  // 6. MAKING BISCUITS OVERLAY
  // ===================================================================
  //
  // Shown only during an active Making-Biscuits mini-game. The two paw
  // pads animate on each BISCUITS_BEAT and the depth bar fills as the
  // trance meter ramps toward MAX_DEPTH. Hides itself on BISCUITS_COMPLETED
  // or BISCUITS_CANCELLED.
  private buildBiscuitsOverlay(): void {
    this.biscuitsPanel = new Rectangle("biscuitsPanel");
    this.biscuitsPanel.width = "340px";
    this.biscuitsPanel.height = "160px";
    this.biscuitsPanel.cornerRadius = 10;
    this.biscuitsPanel.thickness = 2;
    this.biscuitsPanel.color = this.panelBorder;
    this.biscuitsPanel.background = "rgba(18, 10, 28, 0.9)";
    this.biscuitsPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    this.biscuitsPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    this.biscuitsPanel.isHitTestVisible = false;
    this.biscuitsPanel.isVisible = false;
    this.guiTexture.addControl(this.biscuitsPanel);

    // Title
    this.biscuitsTitle = new TextBlock("biscuitsTitle", "Making Biscuits");
    this.biscuitsTitle.color = MINNA_COLOR;
    this.biscuitsTitle.fontSize = 18;
    this.biscuitsTitle.fontFamily = "Georgia, serif";
    this.biscuitsTitle.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    this.biscuitsTitle.top = "10px";
    this.biscuitsTitle.height = "22px";
    this.biscuitsPanel.addControl(this.biscuitsTitle);

    // Left paw pad
    this.biscuitsLeftPad = new Ellipse("biscuitsLeftPad");
    this.biscuitsLeftPad.width = "56px";
    this.biscuitsLeftPad.height = "56px";
    this.biscuitsLeftPad.thickness = 2.5;
    this.biscuitsLeftPad.color = MINNA_COLOR;
    this.biscuitsLeftPad.background = "rgba(0, 255, 136, 0.15)";
    this.biscuitsLeftPad.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    this.biscuitsLeftPad.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    this.biscuitsLeftPad.left = "44px";
    this.biscuitsLeftPad.top = "-8px";
    this.biscuitsPanel.addControl(this.biscuitsLeftPad);
    const leftLabel = new TextBlock("biscuitsLeftLabel", "A");
    leftLabel.color = TEXT_COLOR;
    leftLabel.fontSize = 18;
    leftLabel.fontFamily = this.fontFamily;
    leftLabel.fontWeight = "bold";
    this.biscuitsLeftPad.addControl(leftLabel);

    // Right paw pad
    this.biscuitsRightPad = new Ellipse("biscuitsRightPad");
    this.biscuitsRightPad.width = "56px";
    this.biscuitsRightPad.height = "56px";
    this.biscuitsRightPad.thickness = 2.5;
    this.biscuitsRightPad.color = MINNA_COLOR;
    this.biscuitsRightPad.background = "rgba(0, 255, 136, 0.05)";
    this.biscuitsRightPad.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
    this.biscuitsRightPad.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    this.biscuitsRightPad.left = "-44px";
    this.biscuitsRightPad.top = "-8px";
    this.biscuitsPanel.addControl(this.biscuitsRightPad);
    const rightLabel = new TextBlock("biscuitsRightLabel", "D");
    rightLabel.color = TEXT_COLOR;
    rightLabel.fontSize = 18;
    rightLabel.fontFamily = this.fontFamily;
    rightLabel.fontWeight = "bold";
    this.biscuitsRightPad.addControl(rightLabel);

    // Trance depth bar
    this.biscuitsDepthBg = new Rectangle("biscuitsDepthBg");
    this.biscuitsDepthBg.width = "240px";
    this.biscuitsDepthBg.height = "12px";
    this.biscuitsDepthBg.cornerRadius = 3;
    this.biscuitsDepthBg.thickness = 1;
    this.biscuitsDepthBg.color = "rgba(80, 60, 50, 0.4)";
    this.biscuitsDepthBg.background = "rgba(40, 30, 50, 0.75)";
    this.biscuitsDepthBg.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
    this.biscuitsDepthBg.top = "-34px";
    this.biscuitsPanel.addControl(this.biscuitsDepthBg);

    this.biscuitsDepthFill = new Rectangle("biscuitsDepthFill");
    this.biscuitsDepthFill.width = "0%";
    this.biscuitsDepthFill.height = "100%";
    this.biscuitsDepthFill.cornerRadius = 3;
    this.biscuitsDepthFill.thickness = 0;
    this.biscuitsDepthFill.background = MINNA_COLOR;
    this.biscuitsDepthFill.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    this.biscuitsDepthBg.addControl(this.biscuitsDepthFill);

    // Hint
    this.biscuitsHint = new TextBlock(
      "biscuitsHint",
      formatControlText("Alternate A and D to knead. Press B to stop."),
    );
    this.biscuitsHint.color = DIM_TEXT;
    this.biscuitsHint.fontSize = 12;
    this.biscuitsHint.fontFamily = this.fontFamily;
    this.biscuitsHint.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
    this.biscuitsHint.height = "18px";
    this.biscuitsHint.top = "-14px";
    this.biscuitsPanel.addControl(this.biscuitsHint);
  }

  // ===================================================================
  // 6b. SEPARATION TETHER INDICATOR (#15)
  // ===================================================================
  //
  // Two thin vertical strips pinned to the left and right screen edges.
  // The strip on the side the damped cat is being pulled FROM (= the
  // partner's direction) lights up when PLAYER_SEPARATION_CLAMPED fires;
  // opacity is driven by `1 - damping` so full-band clamp → full alpha.
  // Fades out after ~200ms of silence so the strip stays invisible in
  // normal play.
  private buildSeparationTether(): void {
    const makeStrip = (name: string, side: "left" | "right"): Rectangle => {
      const strip = new Rectangle(name);
      strip.width = "6px";
      strip.height = "55%";
      strip.thickness = 0;
      strip.cornerRadius = 3;
      // Warm copper/brass gradient for consistency with the HUD palette;
      // the color maps to "camera tether is pulling" in the same visual
      // language as the harness integrity bar.
      strip.background = COLORS.COPPER;
      strip.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
      strip.horizontalAlignment =
        side === "left"
          ? Control.HORIZONTAL_ALIGNMENT_LEFT
          : Control.HORIZONTAL_ALIGNMENT_RIGHT;
      strip.left = side === "left" ? "2px" : "-2px";
      strip.alpha = 0;
      strip.isHitTestVisible = false;
      this.guiTexture.addControl(strip);
      return strip;
    };
    this.separationLeftStrip = makeStrip("separationLeftStrip", "left");
    this.separationRightStrip = makeStrip("separationRightStrip", "right");

    this.applyAccessibilityTuning();
  }

  /**
   * Apply the latest clamp telemetry. Called from the event handler
   * registered in `subscribeToEvents`. The strip on the partner's side
   * is what lights up: if the damped cat is to the right of the partner
   * (selfX > partnerX), the tether is pulling left — so the LEFT strip
   * is the signal. Intensity = 1 - damping so a small nudge is a faint
   * strip and full-band clamp is a solid glow.
   */
  private onSeparationClamped(payload: {
    damping: number;
    selfX: number;
    partnerX: number;
  }): void {
    const intensity = Math.max(0, Math.min(1, 1 - payload.damping));
    if (intensity <= 0) return;
    this.separationClampIntensity = intensity;
    this.separationClampSide = payload.selfX > payload.partnerX ? "left" : "right";
    this.separationClampLastEmitAt = performance.now();
  }

  private updateSeparationTether(): void {
    if (!this.separationLeftStrip || !this.separationRightStrip) return;

    // Decay: if no clamp event arrived in the last 200ms, fade the
    // tracked intensity linearly to zero over ~300ms so the strip fades
    // out smoothly once the cats reunite.
    const now = performance.now();
    const sinceLast = now - this.separationClampLastEmitAt;
    if (sinceLast > 200) {
      this.separationClampIntensity = Math.max(
        0,
        this.separationClampIntensity - 0.016 / 0.3,
      );
    }

    const intensity = this.separationClampIntensity;
    // Cap alpha at 0.65 so the strip reads as a signal, not a wall.
    const targetAlpha = intensity * 0.65;
    this.separationLeftStrip.alpha =
      this.separationClampSide === "left" ? targetAlpha : 0;
    this.separationRightStrip.alpha =
      this.separationClampSide === "right" ? targetAlpha : 0;
  }

  /**
   * Show the kneading HUD. Subscribing to `BISCUITS_STARTED` routes to
   * this; exposed as a public method so tests (and future inline
   * callers) can drive the UI directly.
   */
  showBiscuitsOverlay(assistMode: boolean): void {
    this.biscuitsActive = true;
    this.biscuitsDepth = 0;
    this.biscuitsLastPaw = null;
    if (this.biscuitsPanel) this.biscuitsPanel.isVisible = true;
    if (this.biscuitsHint) {
      const stopHint=detectControlGlyphScheme()==="keyboard"?"Press B to stop.":"Move the stick to stop.";
      this.biscuitsHint.text = assistMode
        ? formatControlText(`Hold any paw key. ${stopHint}`)
        : formatControlText(`Alternate A and D to knead. ${stopHint}`);
    }
    this.refreshCueVisibility();
  }

  /** Hide the kneading HUD. */
  hideBiscuitsOverlay(): void {
    this.biscuitsActive = false;
    if (this.biscuitsPanel) this.biscuitsPanel.isVisible = false;
    if (this.biscuitsDepthFill) this.biscuitsDepthFill.width = "0%";
    if (this.biscuitsLeftPad) this.biscuitsLeftPad.background = "rgba(0, 255, 136, 0.05)";
    if (this.biscuitsRightPad) this.biscuitsRightPad.background = "rgba(0, 255, 136, 0.05)";
    this.refreshCueVisibility();
  }

  private updateBiscuitsOverlay(): void {
    if (!this.biscuitsActive) return;
    // Depth fill
    if (this.biscuitsDepthFill) {
      const pct = Math.max(0, Math.min(100, this.biscuitsDepth));
      this.biscuitsDepthFill.width = `${Math.round(pct)}%`;
    }
    // Paw pad flash decay
    const now = performance.now();
    const flashAge = now - this.biscuitsLastPawFlashAt;
    const flashIntensity = Math.max(0, 1 - flashAge / 300);
    const goodBg = `rgba(0, 255, 136, ${0.15 + flashIntensity * 0.55})`;
    const softBg = `rgba(255, 180, 80, ${0.15 + flashIntensity * 0.35})`;
    const idleBg = "rgba(0, 255, 136, 0.08)";
    if (this.biscuitsLeftPad) {
      this.biscuitsLeftPad.background = this.biscuitsLastPaw === "left"
        ? (this.biscuitsLastPawQuality === "good" ? goodBg : softBg)
        : idleBg;
    }
    if (this.biscuitsRightPad) {
      this.biscuitsRightPad.background = this.biscuitsLastPaw === "right"
        ? (this.biscuitsLastPawQuality === "good" ? goodBg : softBg)
        : idleBg;
    }
  }

  // ===================================================================
  // 1. ACTIVE CHARACTER INDICATOR
  // ===================================================================

  private buildCharacterIndicator(): void {
    this.characterPanel = new Rectangle("charPanel");
    this.characterPanel.width = "180px";
    this.characterPanel.height = "44px";
    this.characterPanel.cornerRadius = 6;
    this.characterPanel.thickness = 1.5;
    this.characterPanel.color = this.panelBorder;
    this.characterPanel.background = this.panelBg;
    this.characterPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    this.characterPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    this.characterPanel.left = "16px";
    this.characterPanel.top = "16px";
    this.characterPanel.isHitTestVisible = false;
    this.guiTexture.addControl(this.characterPanel);

    // Colored dot
    this.characterDot = new Ellipse("charDot");
    this.characterDot.width = "12px";
    this.characterDot.height = "12px";
    this.characterDot.thickness = 0;
    this.characterDot.background = MINNA_COLOR;
    this.characterDot.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    this.characterDot.left = "12px";
    this.characterPanel.addControl(this.characterDot);

    // Name label
    this.characterLabel = new TextBlock("charLabel", "Minna");
    this.characterLabel.color = TEXT_COLOR;
    this.characterLabel.fontSize = 18;
    this.characterLabel.fontFamily = this.fontFamily;
    this.characterLabel.fontWeight = "600";
    this.characterLabel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    this.characterLabel.left = "30px";
    this.characterPanel.addControl(this.characterLabel);

    this.updateCharacterIndicator();
  }

  private updateCharacterIndicator(): void {
    if (!this.characterPanel) return;

    // Hide in co-op mode
    this.characterPanel.isVisible = this.soloMode;

    if (this.activePlayer === 1) {
      this.characterLabel!.text = "Minna";
      this.characterDot!.background = MINNA_COLOR;
    } else {
      this.characterLabel!.text = "Basil";
      this.characterDot!.background = BASIL_COLOR;
    }
  }

  // ===================================================================
  // 1b. ENERGY BARS
  // ===================================================================

  private buildEnergyBars(): void {
    // Starts as a compact harness chip. Levels with energy systems call
    // `enableEnergy()`, which expands this into the full two-row energy
    // panel with the harness-integrity bar underneath.
    this.energyPanel = new Rectangle("energyPanel");
    this.energyPanel.width = `${HARNESS_CHIP_WIDTH}px`;
    this.energyPanel.height = `${HARNESS_CHIP_HEIGHT}px`;
    this.energyPanel.cornerRadius = 6;
    this.energyPanel.thickness = 1.5;
    this.energyPanel.color = this.panelBorder;
    this.energyPanel.background = this.panelBg;
    this.energyPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    this.energyPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    this.energyPanel.left = "16px";
    this.energyPanel.top = "58px";
    this.energyPanel.isHitTestVisible = false;
    this.energyPanel.isVisible = true;
    this.guiTexture.addControl(this.energyPanel);

    // --- Minna energy bar ---
    this.minnaEnergyBg = this.createEnergyBarBg("minnaEnergyBg", "-8px");
    this.energyPanel.addControl(this.minnaEnergyBg);
    this.minnaEnergyFill = this.createEnergyBarFill("minnaEnergyFill", MINNA_COLOR);
    this.minnaEnergyBg.addControl(this.minnaEnergyFill);
    this.minnaEnergyLabel = this.createEnergyLabel("minnaEnergyLbl", "Minna", MINNA_COLOR, "-8px");
    this.energyPanel.addControl(this.minnaEnergyLabel);

    // --- Basil energy bar ---
    this.basilEnergyBg = this.createEnergyBarBg("basilEnergyBg", "6px");
    this.energyPanel.addControl(this.basilEnergyBg);
    this.basilEnergyFill = this.createEnergyBarFill("basilEnergyFill", BASIL_COLOR);
    this.basilEnergyBg.addControl(this.basilEnergyFill);
    this.basilEnergyLabel = this.createEnergyLabel("basilEnergyLbl", "Basil", BASIL_COLOR, "6px");
    this.energyPanel.addControl(this.basilEnergyLabel);

    // --- Harness integrity bar (Basil) ---
    // Same width as the energy bars but 8px tall, copper color per
    // spec §5 "HUD" (thin integrity bar underneath Basil's energy bar).
    this.harnessBarBg = new Rectangle("harnessBarBg");
    this.harnessBarBg.width = "152px";
    this.harnessBarBg.height = "8px";
    this.harnessBarBg.cornerRadius = 2;
    this.harnessBarBg.thickness = 0;
    this.harnessBarBg.background = "rgba(40, 30, 50, 0.8)";
    this.harnessBarBg.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
    this.harnessBarBg.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    this.harnessBarBg.left = "-10px";
    this.harnessBarBg.top = "26px";
    // Raised from 0.3 → 0.7 so the harness-integrity bar is actually
    // visible inside the energy panel on levels without energy bars
    // (L0 tutorial). At 0.3 the copper fill read as a ghost line.
    this.harnessBarBg.alpha = 0.7;
    this.energyPanel.addControl(this.harnessBarBg);

    this.harnessBarFill = new Rectangle("harnessBarFill");
    this.harnessBarFill.width = "100%";
    this.harnessBarFill.height = "100%";
    this.harnessBarFill.cornerRadius = 2;
    this.harnessBarFill.thickness = 0;
    this.harnessBarFill.background = COLORS.COPPER;
    this.harnessBarFill.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    this.harnessBarBg.addControl(this.harnessBarFill);

    // Tiny icon/text tag at the left of the bar — icon glyph via text
    // character keeps us dependency-free. Copper cog unicode (⚙) is
    // widely supported; we swap glyph + color on state transitions.
    this.harnessIcon = new TextBlock("harnessIcon", "\u2699");
    this.harnessIcon.color = COLORS.COPPER;
    this.harnessIcon.fontSize = 11;
    this.harnessIcon.fontFamily = this.fontFamily;
    this.harnessIcon.fontWeight = "600";
    this.harnessIcon.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    this.harnessIcon.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    this.harnessIcon.left = "8px";
    this.harnessIcon.top = "26px";
    this.harnessIcon.alpha = 0.6;
    this.energyPanel.addControl(this.harnessIcon);

    this.layoutEnergyPanel();
  }

  private createEnergyBarBg(name: string, top: string): Rectangle {
    const bg = new Rectangle(name);
    bg.width = "152px";
    bg.height = "12px";
    bg.cornerRadius = 3;
    bg.thickness = 0;
    bg.background = "rgba(40, 30, 50, 0.8)";
    bg.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
    bg.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    bg.left = "-10px";
    bg.top = top;
    // Default hidden until `enableEnergy()` flips them on.
    bg.isVisible = false;
    return bg;
  }

  private createEnergyBarFill(name: string, color: string): Rectangle {
    const fill = new Rectangle(name);
    fill.width = "100%";
    fill.height = "100%";
    fill.cornerRadius = 3;
    fill.thickness = 0;
    fill.background = color;
    fill.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    return fill;
  }

  private createEnergyLabel(name: string, text: string, color: string, top: string): TextBlock {
    const label = new TextBlock(name, text);
    label.color = color;
    label.fontSize = 12;
    label.fontFamily = this.fontFamily;
    label.fontWeight = "600";
    label.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    label.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    label.left = "8px";
    label.top = top;
    // Default hidden until `enableEnergy()` flips them on.
    label.isVisible = false;
    return label;
  }

  /**
   * Enable energy bar display. Call once when the level has energy systems.
   */
  enableEnergy(): void {
    this.energyEnabled = true;
    this.layoutEnergyPanel();
  }

  private layoutEnergyPanel(): void {
    const full = this.energyEnabled;

    if (this.energyPanel) {
      this.energyPanel.width = `${full ? ENERGY_PANEL_FULL_WIDTH : HARNESS_CHIP_WIDTH}px`;
      this.energyPanel.height = `${full ? ENERGY_PANEL_FULL_HEIGHT : HARNESS_CHIP_HEIGHT}px`;
      this.energyPanel.thickness = full ? 2.4 : 1.5;
      this.energyPanel.isVisible = true;
    }

    if (this.minnaEnergyBg) this.minnaEnergyBg.isVisible = full;
    if (this.basilEnergyBg) this.basilEnergyBg.isVisible = full;
    if (this.minnaEnergyLabel) this.minnaEnergyLabel.isVisible = full;
    if (this.basilEnergyLabel) this.basilEnergyLabel.isVisible = full;

    if (this.harnessBarBg) {
      this.harnessBarBg.width = full ? "152px" : "126px";
      this.harnessBarBg.left = "-10px";
      this.harnessBarBg.top = full ? "26px" : "0px";
    }
    if (this.harnessIcon) {
      this.harnessIcon.top = full ? "26px" : "0px";
      this.harnessIcon.fontSize = full ? 11 : 13;
      this.harnessIcon.alpha = full ? 0.6 : 0.85;
    }

    this.positionAbilitiesPanel();
  }

  private positionAbilitiesPanel(): void {
    if (!this.abilitiesPanel) return;
    this.abilitiesPanel.top = `${this.energyEnabled ? ABILITY_RAIL_TOP_WITH_ENERGY : ABILITY_RAIL_TOP_WITH_HARNESS_CHIP}px`;
  }

  private updateEnergyBars(): void {
    if (!this.energyEnabled) return;

    // Update fill widths
    if (this.minnaEnergyFill) {
      const pct = Math.max(0, Math.min(1, this.minnaEnergyFraction));
      this.minnaEnergyFill.width = `${Math.round(pct * 100)}%`;
    }
    if (this.basilEnergyFill) {
      const pct = Math.max(0, Math.min(1, this.basilEnergyFraction));
      this.basilEnergyFill.width = `${Math.round(pct * 100)}%`;
    }

    // Depleted flash effect
    this.depletedFlashTimer += 0.016; // ~60fps
    const flash = Math.sin(this.depletedFlashTimer * 8) > 0 ? 1.0 : 0.5;

    if (this.minnaEnergyDepleted) {
      if (this.minnaEnergyFill) this.minnaEnergyFill.alpha = flash;
      if (this.minnaEnergyLabel) this.minnaEnergyLabel.text = "DEPLETED";
    } else {
      if (this.minnaEnergyFill) this.minnaEnergyFill.alpha = 1;
      if (this.minnaEnergyLabel) this.minnaEnergyLabel.text = "Minna";
    }

    if (this.basilEnergyDepleted) {
      if (this.basilEnergyFill) this.basilEnergyFill.alpha = flash;
      if (this.basilEnergyLabel) this.basilEnergyLabel.text = "DEPLETED";
    } else {
      if (this.basilEnergyFill) this.basilEnergyFill.alpha = 1;
      if (this.basilEnergyLabel) this.basilEnergyLabel.text = "Basil";
    }
  }

  // ===================================================================
  // 2. ABILITY COOLDOWN INDICATORS
  // ===================================================================

  private buildAbilitiesPanel(): void {
    this.abilitiesPanel = new StackPanel("abilitiesPanel");
    this.abilitiesPanel.isVertical = true;
    this.abilitiesPanel.width = `${ABILITY_SLOT_SIZE + 8}px`;
    this.abilitiesPanel.height = `${ABILITY_SLOT_SIZE + 12}px`;
    this.abilitiesPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    this.abilitiesPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    this.abilitiesPanel.left = "16px";
    this.positionAbilitiesPanel();
    this.abilitiesPanel.isHitTestVisible = false;
    this.guiTexture.addControl(this.abilitiesPanel);
  }

  /**
   * Register abilities for display. Call once after abilities are set up.
   * Always renders ABILITY_SLOT_COUNT (10) fixed slots — unearned abilities
   * appear as dimmed/locked placeholders so the bar never resizes.
   * @param abilities Array of { id, name, cooldownDuration }
   */
  registerAbilities(abilities: { id: string; name: string; cooldownDuration: number }[]): void {
    // Clear existing slots
    for (const slot of this.abilitySlots.values()) {
      slot.layout.dispose();
    }
    this.abilitySlots.clear();
    this.abilityCooldowns.clear();
    this.prevCooldownRemaining.clear();

    // Build a lookup of registered (earned) abilities by id.
    const earnedMap = new Map(abilities.map((a) => [a.id, a]));

    const orderedAbilities = ABILITY_SLOT_ORDER
      .map((id) => earnedMap.get(id))
      .filter((ability): ability is { id: string; name: string; cooldownDuration: number } => ability !== undefined);
    const extraAbilities = abilities.filter((ability) => !ABILITY_SLOT_ORDER.includes(ability.id));
    const visibleAbilities = [...orderedAbilities, ...extraAbilities];

    if (this.abilitiesPanel) {
      const slotCount = Math.max(visibleAbilities.length, 1);
      this.abilitiesPanel.height = `${slotCount * ABILITY_SLOT_SIZE + 12}px`;
      this.abilitiesPanel.isVisible = visibleAbilities.length > 0;
    }

    for (const ability of visibleAbilities) {
      this.abilityCooldowns.set(ability.id, {
        id: ability.id,
        name: ability.name,
        cooldownRemaining: 0,
        cooldownDuration: ability.cooldownDuration,
        isActive: false,
      });
      this.prevCooldownRemaining.set(ability.id, 0);
      const slot = this.createAbilitySlot(ability.id, ability.name);
      this.abilitySlots.set(ability.id, slot);
    }
  }

  /**
   * Register a per-frame ability-state provider. Called each frame before
   * updateAbilitySlots() to pull live cooldown data from ability holders.
   * This fixes the previously inert updateCooldowns path.
   */
  setAbilityStateProvider(
    provider: (() => Array<{ id: string; cooldownRemaining: number; cooldownDuration: number; isActive: boolean }>) | null
  ): void {
    this.abilityStateProvider = provider;
  }

  private createAbilitySlot(id: string, name: string, locked = false): { layout: Control; bg: Rectangle; fill: Ellipse; label: TextBlock; ring: Ellipse; sweepImage: Image; prevAngleBucket: number; canonicalRingColor: string; flashInProgress: boolean } {
    // Compact edge rail: keep all 10 slots visible without claiming the
    // lower-middle playfield or the bottom dialogue/subtitle lane.
    const bg = new Rectangle(`ability_${id}`);
    bg.width = `${ABILITY_SLOT_SIZE}px`;
    bg.height = `${ABILITY_SLOT_SIZE}px`;
    bg.cornerRadius = 6;
    bg.thickness = this.atmospheric ? 0 : locked ? 1 : 1.5;
    bg.color = locked ? "rgba(100, 80, 60, 0.35)" : this.panelBorder;
    bg.background = this.atmospheric ? "rgba(19, 29, 25, 0.86)" : locked ? "rgba(12, 8, 20, 0.5)" : this.panelBg;
    bg.paddingLeft = "2px";
    bg.paddingRight = "2px";
    bg.paddingTop = "2px";
    bg.paddingBottom = "2px";
    let layout:Control=bg;
    if(this.avoidActors){
      // Allocate the scaled widget's full footprint without interfering with
      // the ring/fill pulse transforms owned by the feedback system.
      const frame=new Rectangle(`abilityFrame_${id}`);
      frame.width=`${ABILITY_SLOT_SIZE}px`;frame.height=`${ABILITY_SLOT_SIZE}px`;
      frame.thickness=0;frame.background="transparent";frame.isHitTestVisible=false;
      frame.addControl(bg);layout=frame;
    }
    this.abilitiesPanel!.addControl(layout);

    // Outer ring (shows cooldown visually via opacity)
    const ring = new Ellipse(`ring_${id}`);
    ring.width = `${ABILITY_RING_SIZE}px`;
    ring.height = `${ABILITY_RING_SIZE}px`;
    ring.thickness = this.atmospheric ? 1.2 : locked ? 1.5 : 3;
    ring.color = locked ? "rgba(80, 60, 50, 0.3)" : this.panelBorder;
    ring.background = "transparent";
    ring.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    ring.top = "-3px";
    bg.addControl(ring);

    // Sweep image (radial cooldown arc — rebuilt only when bucket changes)
    const sweepImage = new Image(`sweep_${id}`, "");
    sweepImage.width = `${ABILITY_RING_SIZE}px`;
    sweepImage.height = `${ABILITY_RING_SIZE}px`;
    sweepImage.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    sweepImage.top = "-3px";
    sweepImage.isHitTestVisible = false;
    sweepImage.source = "";
    bg.addControl(sweepImage);

    // Inner fill (shows active state / cooldown)
    const fill = new Ellipse(`fill_${id}`);
    fill.width = `${ABILITY_FILL_READY_SIZE}px`;
    fill.height = `${ABILITY_FILL_READY_SIZE}px`;
    fill.thickness = 0;
    fill.background = locked ? "rgba(40, 30, 50, 0.3)" : "rgba(196, 155, 60, 0.2)";
    fill.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    fill.top = "-3px";
    bg.addControl(fill);

    // Short label at bottom — curated per-ability so the pills remain legible.
    const shortName = locked ? "" : (ABILITY_SHORT_LABELS[id] ?? (name.length > 6 ? name.slice(0, 5).trimEnd() : name));
    const label = new TextBlock(`lbl_${id}`, shortName);
    label.color = locked ? "rgba(80, 60, 50, 0.3)" : DIM_TEXT;
    label.fontSize = this.atmospheric ? 11 : 9;
    label.fontFamily = this.fontFamily;
    label.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
    label.top = "-1px";
    bg.addControl(label);

    const canonicalRingColor = locked ? "rgba(80, 60, 50, 0.3)" : this.panelBorder;
    return { layout, bg, fill, label, ring, sweepImage, prevAngleBucket: -1, canonicalRingColor, flashInProgress: false };
  }

  /**
   * Brief scale pulse on a slot's ring so the activation reads as a
   * motion cue even if the tint change is missed. Animates via
   * requestAnimationFrame; idempotent — if the slot is already
   * pulsing, the new call restarts the animation from the current
   * scale so rapid re-triggers don't stutter.
   */
  private pulseAbilitySlot(abilityId: string): void {
    const slot = this.abilitySlots.get(abilityId);
    if (!slot) return;
    const ring = slot.ring;
    const start = performance.now();
    const duration = 300;
    const baseScaleX = 1;
    const baseScaleY = 1;
    const peak = 1.35;
    const step = (now: number): void => {
      const t = Math.min(1, (now - start) / duration);
      // Ease: bump to peak at t=0.4 then back to 1.0 at t=1.
      const curve = t < 0.4
        ? baseScaleX + (peak - baseScaleX) * (t / 0.4)
        : peak - (peak - baseScaleX) * ((t - 0.4) / 0.6);
      ring.scaleX = curve;
      ring.scaleY = curve;
      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        ring.scaleX = baseScaleX;
        ring.scaleY = baseScaleY;
      }
    };
    requestAnimationFrame(step);
  }

  /**
   * Flash the owning slot red for ~300ms on ABILITY_BLOCKED.
   * No-ops safely when rAF is absent (headless/NullEngine).
   */
  private flashAbilitySlotRed(abilityId: string): void {
    const slot = this.abilitySlots.get(abilityId);
    if (!slot) return;
    if (typeof requestAnimationFrame !== "function") return;
    // Guard: if a flash is already in progress, don't start another one —
    // doing so would capture red as "originalColor" and never restore the true color.
    if (slot.flashInProgress) return;
    slot.flashInProgress = true;
    const ring = slot.ring;
    ring.color = "#ff2222";
    const start = performance.now();
    const duration = 300;
    const step = (now: number): void => {
      const t = Math.min(1, (now - start) / duration);
      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        ring.color = slot.canonicalRingColor;
        slot.flashInProgress = false;
      }
    };
    requestAnimationFrame(step);
  }

  private updateAbilitySlots(): void {
    // Pull live data from provider if wired (fixes the inert updateCooldowns path)
    if (this.abilityStateProvider) {
      const states = this.abilityStateProvider();
      for (const ab of states) {
        const state = this.abilityCooldowns.get(ab.id);
        if (state) {
          const prev = this.prevCooldownRemaining.get(ab.id) ?? state.cooldownRemaining;
          // Pulse slot when cooldown just crossed to ready
          if (crossedReady(prev, ab.cooldownRemaining)) {
            this.pulseAbilitySlot(ab.id);
          }
          state.cooldownRemaining = ab.cooldownRemaining;
          state.isActive = ab.isActive;
          this.prevCooldownRemaining.set(ab.id, ab.cooldownRemaining);
        }
      }
    }

    for (const [id, state] of this.abilityCooldowns) {
      const slot = this.abilitySlots.get(id);
      if (!slot) continue;

      const { progress } = cooldownStateForSlot(state.cooldownRemaining, state.cooldownDuration, state.isActive);
      const angleDeg = progressToSweepAngle(progress);
      const bucket = Math.round(angleDeg / 10) * 10;

      // Rebuild sweep image only when bucket changes (avoids per-frame texture churn)
      if (bucket !== slot.prevAngleBucket) {
        slot.prevAngleBucket = bucket;
        const sweepColor = state.id.startsWith("minna") || ["auraSight", "shadowSlip", "calmingResonance"].includes(state.id)
          ? MINNA_COLOR
          : BASIL_COLOR;
        slot.sweepImage.source = buildSweepDataUri(bucket, sweepColor, ABILITY_RING_SIZE);
      }

      if (state.isActive) {
        slot.ring.color = state.id.startsWith("minna") || ["auraSight", "shadowSlip", "calmingResonance"].includes(state.id)
          ? MINNA_COLOR
          : BASIL_COLOR;
        slot.fill.background = "rgba(255, 255, 255, 0.15)";
        slot.label.color = TEXT_COLOR;
      } else if (state.cooldownRemaining > 0) {
        slot.ring.color = DIM_TEXT;
        // Fill is now a small dot at fixed size (not shrinking — sweep handles progress)
        slot.fill.width = `${ABILITY_FILL_READY_SIZE}px`;
        slot.fill.height = `${ABILITY_FILL_READY_SIZE}px`;
        slot.fill.background = "rgba(100, 60, 20, 0.4)";
        slot.label.color = DIM_TEXT;
      } else {
        slot.ring.color = this.panelBorder;
        slot.fill.width = `${ABILITY_FILL_READY_SIZE}px`;
        slot.fill.height = `${ABILITY_FILL_READY_SIZE}px`;
        slot.fill.background = "rgba(196, 155, 60, 0.2)";
        slot.label.color = TEXT_COLOR;
      }
    }
  }

  // ===================================================================
  // 3. PUZZLE PROGRESS
  // ===================================================================

  private buildPuzzleProgress(): void {
    this.puzzlePanel = new Rectangle("puzzlePanel");
    this.puzzlePanel.width = "220px";
    this.puzzlePanel.height = "40px";
    this.puzzlePanel.cornerRadius = 6;
    this.puzzlePanel.thickness = 1.5;
    this.puzzlePanel.color = this.panelBorder;
    this.puzzlePanel.background = this.panelBg;
    this.puzzlePanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    this.puzzlePanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    this.puzzlePanel.top = "16px";
    this.puzzlePanel.isHitTestVisible = false;
    this.puzzlePanel.isVisible = false;
    this.guiTexture.addControl(this.puzzlePanel);

    this.puzzleLabel = new TextBlock("puzzleLabel", "");
    this.puzzleLabel.color = TEXT_COLOR;
    this.puzzleLabel.fontSize = 17;
    this.puzzleLabel.fontFamily = this.fontFamily;
    this.puzzlePanel.addControl(this.puzzleLabel);
  }

  /**
   * Set the puzzle progress display.
   * @param current Current puzzle number (1-based)
   * @param total Total puzzles in this level
   */
  setPuzzleProgress(current: number, total: number): void {
    this.currentPuzzle = current;
    this.totalPuzzles = total;
    this.puzzleVisible = total > 0;

    if (this.puzzlePanel) {
      this.puzzlePanel.isVisible = this.puzzleVisible;
      if (this.puzzleLabel) {
        this.puzzleLabel.text = `Puzzle ${current} / ${total}`;
      }
    }
  }

  private hidePuzzleProgress(): void {
    this.puzzleVisible = false;
    if (this.puzzlePanel) {
      this.puzzlePanel.isVisible = false;
    }
  }

  // ===================================================================
  // 3b. ALWAYS-ON OBJECTIVE LINE
  // ===================================================================
  //
  // Vertical band layout (top-centre stack, no intersections):
  //   Puzzle counter : top 16px, height 40px → band [16, 56]
  //   Objective line : top 64px, height 28px → band [64, 92]  (8px gap below puzzle)
  //   Prompt panel   : BOTTOM-anchored at -120px               (no overlap with top stack)
  //
  // Intentionally excluded from toggleHints — the objective tells the
  // player WHAT to do (load-bearing), whereas the prompt gives HOW detail
  // that experienced players may wish to suppress.
  private buildObjectiveLine(): void {
    this.objectivePanel = new Rectangle("objectivePanel");
    this.objectivePanel.width = "320px";
    this.objectivePanel.height = "28px";
    this.objectivePanel.cornerRadius = 4;
    this.objectivePanel.thickness = 1;
    this.objectivePanel.color = this.panelBorder;
    this.objectivePanel.background = this.panelBg;
    this.objectivePanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    this.objectivePanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    this.objectivePanel.top = "64px";
    this.objectivePanel.isHitTestVisible = false;
    this.objectivePanel.isVisible = false;
    this.guiTexture.addControl(this.objectivePanel);

    this.objectiveLabel = new TextBlock("objectiveLabel", "");
    this.objectiveLabel.color = TEXT_COLOR;
    this.objectiveLabel.fontSize = 13;
    this.objectiveLabel.fontFamily = this.fontFamily;
    this.objectiveLabel.paddingLeft = "10px";
    this.objectiveLabel.paddingRight = "10px";
    this.objectivePanel.addControl(this.objectiveLabel);
  }

  /** Show the always-on objective line (not affected by hints toggle). */
  setObjective(text: string): void {
    if (!text) {
      this.clearObjective();
      return;
    }
    if (this.objectivePanel && this.objectiveLabel) {
      this.objectiveLabel.text = text;
      this.objectivePanel.isVisible = true;
    }
  }

  /** Hide and clear the objective line. */
  clearObjective(): void {
    if (this.objectivePanel && this.objectiveLabel) {
      this.objectiveLabel.text = "";
      this.objectivePanel.isVisible = false;
    }
  }

  // ===================================================================
  // 4. INTERACTION PROMPTS
  // ===================================================================

  private buildInteractionPrompt(): void {
    this.promptPanel = new Rectangle("promptPanel");
    this.promptPanel.width = "460px";
    this.promptPanel.adaptHeightToChildren = true;
    this.promptPanel.cornerRadius = 6;
    this.promptPanel.thickness = 1.5;
    this.promptPanel.color = this.panelBorder;
    this.promptPanel.background = this.panelBg;
    this.promptPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    // BOTTOM-anchored per the vertical-band layout documented above
    // buildObjectiveLine(). This panel previously used TOP at 68px,
    // which landed it exactly on the objective line's band [64, 92] —
    // the two texts rendered stacked on each other whenever a puzzle
    // set both (2026-07-10 visual review, finding: HUD prompt overlap).
    // -120px keeps it clear of the dialogue/subtitle lane at the very
    // bottom and the lower-middle traversal view.
    this.promptPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
    this.promptPanel.top = "-120px";
    this.promptPanel.isHitTestVisible = false;
    this.promptPanel.isVisible = false;
    this.guiTexture.addControl(this.promptPanel);

    this.promptLabel = new TextBlock("promptLabel", "");
    this.promptLabel.color = TEXT_COLOR;
    this.promptLabel.fontSize = 15;
    this.promptLabel.fontFamily = this.fontFamily;
    applyWrappedTextLayout(this.promptLabel);
    this.promptLabel.paddingLeft = "14px";
    this.promptLabel.paddingRight = "14px";
    this.promptLabel.paddingTop = "10px";
    this.promptLabel.paddingBottom = "10px";
    this.promptPanel.addControl(this.promptLabel);

    this.promptContentContainer = new StackPanel("promptContentContainer");
    this.promptContentContainer.isVertical = true;
    this.promptContentContainer.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    this.promptContentContainer.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    this.promptContentContainer.spacing = 6;
    this.promptContentContainer.paddingLeft = "14px";
    this.promptContentContainer.paddingRight = "14px";
    this.promptContentContainer.paddingTop = "10px";
    this.promptContentContainer.paddingBottom = "10px";
    this.promptContentContainer.isVisible = false;
    this.promptPanel.addControl(this.promptContentContainer);
  }

  showPrompt(text: string): void {
    this.lastPromptText = text;
    if (!this.hintsEnabled) return;
    if (this.promptPanel) {
      if (this.controllerActive && this.promptContentContainer && this.promptLabel) {
        this.promptLabel.isVisible = false;
        this.promptContentContainer.isVisible = true;
        this.renderRichPrompt(text);
      } else if (this.promptLabel && this.promptContentContainer) {
        this.promptContentContainer.isVisible = false;
        this.promptLabel.isVisible = true;
        this.promptLabel.text = formatControlText(text);
      }
      this.promptPanel.isVisible = true;
    }
  }

  hidePrompt(): void {
    if (this.promptPanel) {
      this.promptPanel.isVisible = false;
    }
  }

  /** Toggle the puzzle-hint layer. Default state is ON. */
  toggleHints(): void {
    this.hintsEnabled = !this.hintsEnabled;
    try {
      if (typeof window !== "undefined") {
        window.localStorage?.setItem(
          "catsOfChronos.hintsEnabled",
          String(this.hintsEnabled),
        );
      }
    } catch {
      /* ignore */
    }
    if (this.hintsEnabled && this.lastPromptText) {
      this.showPrompt(this.lastPromptText);
    } else {
      this.hidePrompt();
    }
    this.refreshCueVisibility();
    gameEvents.emit(GameEvents.HUD_TOAST, {
      message: this.hintsEnabled
        ? "Hints: ON — puzzle prompts visible. Press H to hide."
        : "Hints: OFF — explore freely. Press H for puzzle prompts.",
      color: "#c49b3c",
    });
  }

  public setControllerActive(active: boolean): void {
    if (this.controllerActive === active) return;
    this.controllerActive = active;
    this.renderCues();
    if (this.lastPromptText && this.promptPanel && this.promptPanel.isVisible) {
      this.showPrompt(this.lastPromptText);
    }
  }

  private renderRichPrompt(text: string): void {
    if (!this.promptContentContainer) return;
    this.promptContentContainer.clearControls();

    const scheme = typeof navigator !== "undefined" && typeof navigator.getGamepads === "function"
      ? "xbox"
      : "xbox";

    const formatted = formatControlText(text);
    const parts = formatted.split(/\s*\|\s*/);

    for (const part of parts) {
      if (!part.trim()) continue;

      const row = new StackPanel("promptRow");
      row.isVertical = false;
      row.height = "36px";
      row.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
      row.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
      row.spacing = 6;
      this.promptContentContainer.addControl(row);

      const segments = this.parsePromptSegments(part);
      for (const seg of segments) {
        if (seg.type === "text") {
          const scale = AccessibilitySystem.getInstance().state.fontScale;
          const txt = new TextBlock("promptSegText", seg.content);
          txt.color = TEXT_COLOR;
          txt.fontSize = 14 * scale;
          txt.fontFamily = this.fontFamily;
          txt.resizeToFit = true;
          row.addControl(txt);
        } else {
          const svgData = this.getButtonSVG(seg.content, scheme);
          const img = new Image("promptSegBtn", svgData);
          const w = seg.content.length > 2 ? Math.min(100, seg.content.length * 8 + 12) : 30;
          img.width = `${w}px`;
          img.height = "30px";
          img.stretch = Image.STRETCH_UNIFORM;
          row.addControl(img);
        }
      }
    }
  }

  private parsePromptSegments(text: string): { type: "text" | "button"; content: string }[] {
    const regex = /\[(.*?)\]/g;
    const segments: { type: "text" | "button"; content: string }[] = [];
    let lastIndex = 0;
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        segments.push({ type: "text", content: text.substring(lastIndex, match.index) });
      }
      segments.push({ type: "button", content: match[1] });
      lastIndex = regex.lastIndex;
    }
    
    if (lastIndex < text.length) {
      segments.push({ type: "text", content: text.substring(lastIndex) });
    }
    
    return segments;
  }

  private getButtonSVG(label: string, scheme: "xbox" | "playstation", keycap = false): string {
    const isPlaystation = scheme === "playstation";
    const borderColor = "#c49b3c"; // Brass border
    const bgColor = "#0e0618"; // Deep gothic midnight purple background
    const textColor = isPlaystation ? "#a2cffe" : "#f5eedb"; // Cool blue for PS, cream for Xbox
    
    let brandColor = "#c49b3c";
    if (isPlaystation) {
      if (label === "Cross" || label === "Cross / Square") brandColor = "#4cc3f7";
      else if (label === "Circle") brandColor = "#ff5252";
      else if (label === "Square") brandColor = "#e040fb";
      else if (label === "Triangle") brandColor = "#00e676";
    } else {
      if (label === "A") brandColor = "#00e676";
      else if (label === "B") brandColor = "#ff5252";
      else if (label === "X") brandColor = "#4cc3f7";
      else if (label === "Y") brandColor = "#ffd600";
    }

    const width = label.length > 2 ? Math.min(100, label.length * 8 + 12) : 30;
    const height = 30;
    // Keyboard keys are square-ish keycaps; controller face buttons are round.
    const rx = keycap || label.length > 2 ? 6 : 15;
    const ry = rx;

    // Arrow keys are drawn, not typed: many system fonts used for SVG
    // images lack the arrow glyphs and render them as "..".
    const ARROWS: Record<string, string> = { "←": "19,9 11,15 19,21", "→": "11,9 19,15 11,21", "↑": "9,19 15,11 21,19", "↓": "9,11 15,19 21,11" };
    const glyph = ARROWS[label]
      ? `<polygon points="${ARROWS[label]}" fill="${textColor}" />`
      : `<text x="${width / 2}" y="${height / 2 + 1}" font-family="'Segoe UI', Roboto, Helvetica, Arial, sans-serif" font-size="10" font-weight="bold" fill="${textColor}" text-anchor="middle" dominant-baseline="middle">${label}</text>`;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
      <rect x="1.5" y="1.5" width="${width - 3}" height="${height - 3}" rx="${rx}" ry="${ry}" fill="${bgColor}" stroke="${borderColor}" stroke-width="2" />
      <rect x="3.5" y="3.5" width="${width - 7}" height="${height - 7}" rx="${Math.max(1, rx - 2)}" ry="${Math.max(1, ry - 2)}" fill="none" stroke="${brandColor}" stroke-width="0.8" stroke-dasharray="2,2" />
      ${glyph}
    </svg>`;
    
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }

  // ===================================================================
  // 4b. TUTORIAL CUES
  // ===================================================================
  //
  // Short "who · what to press" chips (ui/tutorialCues.ts). The objective
  // cue floats above the thing the step is about, so the instruction sits
  // where the player is looking instead of over the middle of the screen;
  // when that thing is off-screen the chip rides the nearest edge with an
  // arrow. Both chips hide during cutscenes and with the hints toggle.

  private buildCues(): void {
    const make = (name: string): CueSlot => {
      const panel = new Rectangle(`${name}CuePanel`);
      panel.adaptWidthToChildren = true;
      panel.height = "40px";
      panel.cornerRadius = 8;
      panel.thickness = 1.5;
      panel.color = this.panelBorder;
      panel.background = this.panelBg;
      panel.isHitTestVisible = false;
      panel.isVisible = false;
      this.guiTexture.addControl(panel);
      const row = new StackPanel(`${name}CueRow`);
      row.isVertical = false;
      row.height = "40px";
      row.spacing = 6;
      row.paddingLeft = "10px";
      row.paddingRight = "10px";
      panel.addControl(row);
      return { def: null, anchor: null, panel, row, side: 0 };
    };
    const objective = make("objective");
    const controls = make("controls");
    controls.panel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    controls.panel.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
    controls.panel.top = "-24px";
    this.cueSlots = { objective, controls };
    this.cueProxy = new TransformNode("hudCueAnchor", this.scene);
    this.placeObjectiveCue(objective);
  }

  /** Set or clear a cue slot (HUD_SET_CUE). */
  setCue(slot: "objective" | "controls", def: CueDef | "controls" | null, anchor?: { x: number; y: number; z: number }): void {
    const s = this.cueSlots?.[slot];
    if (!s) return;
    const hadAnchor = s.anchor !== null;
    if (anchor) {
      if (s.anchor) s.anchor.set(anchor.x, anchor.y, anchor.z);
      else s.anchor = new Vector3(anchor.x, anchor.y, anchor.z);
    } else {
      s.anchor = null;
    }
    if (slot === "objective" && hadAnchor !== (s.anchor !== null)) this.placeObjectiveCue(s);
    // Same cue, moved anchor (a kitten wandering, an opossum trotting):
    // updateCues follows it; no need to rebuild the chip.
    if (s.def === def) return;
    s.def = def;
    this.renderCues();
  }

  /** An anchored objective chip rides its anchor; an unanchored one sits
   *  under the objective line at the top. */
  private placeObjectiveCue(s: CueSlot): void {
    if (s.anchor && this.cueProxy) {
      s.panel.linkWithMesh(this.cueProxy);
      s.panel.linkOffsetY = "-4px";
    } else {
      s.panel.linkWithMesh(null);
      s.panel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
      s.panel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
      s.panel.left = "0px";
      s.panel.top = "100px";
    }
  }

  /** Current resolved segments of a cue slot (tests and QA read this). */
  getCueSegments(slot: "objective" | "controls"): CueSegment[] {
    return this.cueSlots?.[slot].segments ?? [];
  }

  private renderCues(): void {
    if (!this.cueSlots) return;
    const ctx = {
      soloMode: this.soloMode,
      activePlayer: this.activePlayer,
      scheme: this.controllerActive ? detectControlGlyphScheme() : ("keyboard" as const),
    };
    for (const s of Object.values(this.cueSlots)) {
      s.row.clearControls();
      s.segments = s.def === null ? [] : s.def === "controls" ? resolveControlsCue(ctx) : resolveCue(s.def, ctx);
      if (s.side !== 0) this.addCueText(s.row, s.side < 0 ? "◀" : "▶", this.panelBorder);
      for (const seg of s.segments) {
        if (seg.kind === "text") this.addCueText(s.row, seg.text, TEXT_COLOR);
        else if (seg.kind === "cat") this.addCueText(s.row, cueCatName(seg.cat), cueCatColor(seg.cat), true);
        else {
          const img = new Image("cueKey", this.getButtonSVG(seg.label, "xbox", ctx.scheme === "keyboard"));
          img.width = `${seg.label.length > 2 ? Math.min(100, seg.label.length * 8 + 12) : 30}px`;
          img.height = "30px";
          img.stretch = Image.STRETCH_UNIFORM;
          s.row.addControl(img);
        }
      }
    }
    this.refreshCueVisibility();
  }

  private addCueText(row: StackPanel, text: string, color: string, bold = false): void {
    const scale = AccessibilitySystem.getInstance().state.fontScale;
    const t = new TextBlock("cueText", text);
    t.color = color;
    t.fontSize = 16 * scale;
    t.fontFamily = this.fontFamily;
    if (bold) t.fontWeight = "bold";
    t.resizeToFit = true;
    row.addControl(t);
  }

  private refreshCueVisibility(): void {
    if (!this.cueSlots) return;
    for (const s of Object.values(this.cueSlots)) {
      s.panel.isVisible = s.segments !== undefined && s.segments.length > 0 && this.hintsEnabled && !this.cutsceneActive && !(this.avoidActors && this.biscuitsActive);
    }
  }

  /** Keep the objective chip over its anchor, clamped inside the screen. */
  private updateCues(): void {
    const s = this.cueSlots?.objective;
    const cam = this.scene.activeCamera;
    if (!s || !s.anchor || !this.cueProxy || !cam || !s.panel.isVisible) return;
    const engine = this.scene.getEngine();
    const w = engine.getRenderWidth();
    const h = engine.getRenderHeight();
    const vp = cam.viewport.toGlobal(w, h);
    const view = cam.getViewMatrix(true);
    const proj = cam.getProjectionMatrix(true);
    const p = Vector3.Project(s.anchor, Matrix.IdentityReadOnly, view.multiply(proj), vp);
    // A margin for the anchor alone clips wide chips at the screen edge.
    // Include the measured panel half-width and reserve the ability rail.
    const textureWidth = this.guiTexture.getSize().width;
    const panelHalfWidth = s.panel.widthInPixels * w / textureWidth / 2;
    const leftMargin = Math.max(w * 0.12, panelHalfWidth + w * 0.075);
    const rightMargin = Math.max(w * 0.12, panelHalfWidth + w * 0.02);
    let side: -1 | 0 | 1 = 0;
    let x = p.x;
    if (x < leftMargin) { x = leftMargin; side = -1; } else if (x > w - rightMargin) { x = w - rightMargin; side = 1; }
    const scaleX = w / textureWidth;
    const scaleY = h / this.guiTexture.getSize().height;
    const obstacles = [this.puzzlePanel, this.objectivePanel,
      this.activeToasts.length > 0 ? this.toastContainer : null]
      .filter((panel): panel is Rectangle | StackPanel => panel !== null && panel.isVisible)
      .map((panel) => ({
        left: (panel.centerX - panel.widthInPixels / 2) * scaleX,
        top: (panel.centerY - panel.heightInPixels / 2) * scaleY,
        width: panel.widthInPixels * scaleX,
        height: panel.heightInPixels * scaleY,
      }));
    // linkOffsetY moves the rendered center relative to the projected proxy.
    // Clamp the actual panel rectangle, then convert back to the proxy point.
    const offsetY = s.panel.linkOffsetYInPixels * scaleY;
    let centerY = placeCueBelowTopHud({ x, y: p.y + offsetY },
      { width: s.panel.widthInPixels * scaleX, height: s.panel.heightInPixels * scaleY }, h, obstacles);
    if (this.avoidActors) {
      const width=s.panel.widthInPixels*scaleX,height=s.panel.heightInPixels*scaleY;
      const placed=placePanelAvoidingRects({left:x-width/2,top:centerY-height/2,width,height},{width:w,height:h},
        [...this.actorRects,...this.fixedHudRects(),...(this.headerRect?[this.headerRect]:[])]);
      if(placed){x=placed.left+width/2;centerY=placed.top+height/2;}
    }
    const y = centerY - offsetY;
    if (side !== s.side) {
      s.side = side;
      this.renderCues();
    }
    if (x === p.x && y === p.y) {
      this.cueProxy.position.copyFrom(s.anchor);
    } else {
      Vector3.UnprojectToRef(new Vector3(x - vp.x, y - vp.y, p.z), vp.width, vp.height, Matrix.IdentityReadOnly, view, proj, this.cueProxy.position);
    }
  }

  // ===================================================================
  // 5. TOAST NOTIFICATIONS
  // ===================================================================

  private buildToastContainer(): void {
    this.toastContainer = new StackPanel("toastContainer");
    this.toastContainer.isVertical = true;
    this.toastContainer.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
    this.toastContainer.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    this.toastContainer.left = "-16px";
    this.toastContainer.top = "16px";
    this.toastContainer.width = "360px";
    this.toastContainer.isHitTestVisible = false;
    this.guiTexture.addControl(this.toastContainer);
  }

  /**
   * Show a toast notification that auto-dismisses after 3 seconds.
   */
  showToast(message: string, color?: string): void {
    const now = performance.now();
    const displayMessage = formatControlText(message);
    const existing = this.activeToasts.find(
      (toast) => !toast.fadingOut && toast.message === displayMessage,
    );
    if (existing && now - existing.startTime < TOAST_DEDUPE_WINDOW_MS) {
      existing.startTime = now;
      existing.container.alpha = 1;
      return;
    }

    // (Removed stale prompt panel top shift. Toasts are top-right, prompts are bottom-center)
    while (this.activeToasts.length >= (this.avoidActors ? 1 : MAX_ACTIVE_TOASTS)) {
      const oldest = this.activeToasts.shift();
      oldest?.container.dispose();
    }

    const container = new Rectangle(`toast_${Date.now()}`);
    container.width = "340px";
    container.adaptHeightToChildren = true;
    container.cornerRadius = 6;
    container.thickness = 1.5;
    container.color = color ?? this.panelBorder;
    container.background = this.panelBg;
    container.alpha = 0;
    container.paddingBottom = "6px";
    container.isHitTestVisible = false;

    const scale = AccessibilitySystem.getInstance().state.fontScale;
    const text = new TextBlock("toastText", displayMessage);
    text.color = color ?? TEXT_COLOR;
    text.fontSize = 15 * scale;
    text.fontFamily = this.fontFamily;
    applyWrappedTextLayout(text);
    text.paddingLeft = "10px";
    text.paddingRight = "10px";
    text.paddingTop = "8px";
    text.paddingBottom = "8px";
    container.addControl(text);

    this.toastContainer!.addControl(container);

    const entry: ToastEntry = {
      container,
      startTime: now,
      fadingOut: false,
      message: displayMessage,
    };
    this.activeToasts.push(entry);

    // Fade in
    this.animateAlpha(container, 0, 1, 200);
  }

  private updateToasts(): void {
    const now = performance.now();
    const toRemove: ToastEntry[] = [];

    for (const toast of this.activeToasts) {
      const elapsed = now - toast.startTime;

      if (!toast.fadingOut && elapsed >= TOAST_DURATION_MS) {
        // Start fade out
        toast.fadingOut = true;
        this.animateAlpha(toast.container, 1, 0, TOAST_FADE_MS, () => {
          toast.container.dispose();
        });
      }

      if (toast.fadingOut && elapsed >= TOAST_DURATION_MS + TOAST_FADE_MS + 50) {
        toRemove.push(toast);
      }
    }

    for (const t of toRemove) {
      const idx = this.activeToasts.indexOf(t);
      if (idx >= 0) this.activeToasts.splice(idx, 1);
    }

    // (Removed stale prompt panel reset)
  }

  // ===================================================================
  // EVENT SUBSCRIPTIONS
  // ===================================================================

  private subscribeToEvents(): void {
    // Player switched (solo mode toggle)
    this.unsubscribers.push(
      gameEvents.on(GameEvents.PLAYER_SWITCHED, (player) => {
        this.activePlayer = player;
        this.updateCharacterIndicator();
        this.renderCues();
      })
    );
    this.unsubscribers.push(
      // Nothing else tells the HUD about F2: without this the solo
      // character indicator never appeared and cues kept co-op keys.
      gameEvents.on(GameEvents.INPUT_MODE_CHANGED, ({ solo }) => this.setSoloMode(solo)),
      gameEvents.on(GameEvents.HUD_SET_CUE, ({ slot, cue, anchor }) => {
        this.setCue(slot, cue, anchor);
      }),
      gameEvents.on(GameEvents.CUTSCENE_STARTED, () => {
        this.cutsceneActive = true;
        if(this.avoidActors)this.guiTexture.rootContainer.isVisible=false;
        this.refreshCueVisibility();
      }),
      gameEvents.on(GameEvents.CUTSCENE_ENDED, () => {
        this.cutsceneActive = false;
        if(this.avoidActors)this.guiTexture.rootContainer.isVisible=true;
        this.refreshCueVisibility();
      }),
    );

    // Ability activated — tint change plus a short scale pulse on the
    // slot so the feedback reads at a glance. Gemini 3.1-pro fluidity
    // review flagged the prior tint-only treatment as "HUD ability
    // icons remain static when abilities are used." The pulse animates
    // the ring scale from 1.0 → 1.35 → 1.0 over ~300ms.
    this.unsubscribers.push(
      gameEvents.on(GameEvents.ABILITY_ACTIVATED, (data) => {
        const state = this.abilityCooldowns.get(data.abilityId);
        if (state) {
          state.isActive = true;
        }
        this.pulseAbilitySlot(data.abilityId);
      })
    );

    // Ability deactivated
    this.unsubscribers.push(
      gameEvents.on(GameEvents.ABILITY_DEACTIVATED, (data) => {
        const state = this.abilityCooldowns.get(data.abilityId);
        if (state) {
          state.isActive = false;
          state.cooldownRemaining = state.cooldownDuration;
        }
      })
    );

    // Ability blocked — red flash on the owning slot + contextual toast
    this.unsubscribers.push(
      gameEvents.on(GameEvents.ABILITY_BLOCKED, (data) => {
        this.flashAbilitySlotRed(data.abilityId);
        const toast = blockedToastFor(data.reason);
        if (toast) {
          this.showToast(toast, "#ff4444");
        }
      })
    );

    // Puzzle progressed
    this.unsubscribers.push(
      gameEvents.on(GameEvents.PUZZLE_PROGRESSED, (data) => {
        const { current, total } = data;
        if (typeof current === 'number' && typeof total === 'number') {
          this.setPuzzleProgress(current, total);
        }
      })
    );

    // Puzzle solved — show toast but DON'T hide the prompt. The level
    // shell's refreshPrompt() fires on the same event and updates the
    // prompt text to the next puzzle. Hiding here would race with
    // refreshPrompt and kill the new prompt.
    this.unsubscribers.push(
      gameEvents.on(GameEvents.PUZZLE_SOLVED, () => {
        this.showToast("Puzzle Complete!", MINNA_COLOR);
      })
    );

    // Level started — reset puzzle progress
    // NOTE: LEVEL_STARTED payload is a string (level name). The totalPuzzles
    // field was never actually emitted. This listener is kept for future use
    // if LEVEL_STARTED is extended with richer data.
    this.unsubscribers.push(
      gameEvents.on(GameEvents.LEVEL_STARTED, (_levelName) => {
        // TODO: wire totalPuzzles into LEVEL_STARTED payload or separate event
      })
    );

    // Co-op ability unlocked
    this.unsubscribers.push(
      gameEvents.on(GameEvents.COOP_ABILITY_UNLOCKED, (data) => {
        this.showToast(`New Ability: ${data.abilityName ?? 'Unknown'}`, BASIL_COLOR);
      })
    );

    // Energy changed
    this.unsubscribers.push(
      gameEvents.on(GameEvents.ENERGY_CHANGED, (data) => {
        const fraction = data.max > 0 ? data.current / data.max : 0;
        if (data.character === 'minna') {
          this.minnaEnergyFraction = fraction;
        } else if (data.character === 'basil') {
          this.basilEnergyFraction = fraction;
        }
      })
    );

    // Energy depleted
    this.unsubscribers.push(
      gameEvents.on(GameEvents.ENERGY_DEPLETED, (data) => {
        if (data.character === 'minna') {
          this.minnaEnergyDepleted = true;
        } else if (data.character === 'basil') {
          this.basilEnergyDepleted = true;
        }
      })
    );

    // Energy restored
    this.unsubscribers.push(
      gameEvents.on(GameEvents.ENERGY_RESTORED, (data) => {
        if (data.character === 'minna') {
          this.minnaEnergyDepleted = false;
        } else if (data.character === 'basil') {
          this.basilEnergyDepleted = false;
        }
      })
    );

    // Harness integrity (Wounds/Medicine)
    this.unsubscribers.push(
      gameEvents.on(GameEvents.HARNESS_INTEGRITY_CHANGED, (data) => {
        this.harnessFraction = data.max > 0 ? data.current / data.max : 0;
        this.harnessState = data.state;
      }),
    );
    this.unsubscribers.push(
      gameEvents.on(GameEvents.HARNESS_STATE_CHANGED, (data) => {
        this.harnessState = data.next;
        if (data.next === "limping") {
          this.showToast("Harness critical — cooling heavy systems.", "#cc4422");
        } else if (data.next === "venting" && data.prev !== "limping") {
          this.showToast("Harness venting — paws get sharper.", "#cc8833");
        }
      }),
    );
    // Heal-pulse VFX: brief alpha flash on the bar each time the
    // harness heals. The timer decays per frame in updateHarnessBar.
    this.unsubscribers.push(
      gameEvents.on(GameEvents.HARNESS_HEALED, () => {
        this.harnessHealPulseTimer = 0.5;
      }),
    );
    // Phase 5 light-fail: surface a distinct fail banner so the player
    // understands the checkpoint reload was a retry, not a random teleport.
    // HUD owns the SINGLE fail toast (MC3) — BaseLevel only restores.
    this.unsubscribers.push(
      gameEvents.on(GameEvents.PUZZLE_FAILED, (data) => {
        this.showToast(
          data.reason === "harness"
            ? "Harness failed — retrying from checkpoint."
            : "Escort lost — retrying from checkpoint.",
          "#cc4422",
        );
      }),
    );

    // Cutscene/Dialogue active - shift prompt up to avoid dialogue overlap
    this.unsubscribers.push(
      gameEvents.on(GameEvents.CUTSCENE_STARTED, () => {
        if (this.promptPanel) {
          this.promptPanel.top = "-220px";
        }
      })
    );

    this.unsubscribers.push(
      gameEvents.on(GameEvents.CUTSCENE_ENDED, () => {
        if (this.promptPanel) {
          this.promptPanel.top = "-120px";
        }
      })
    );

    // Interaction prompt show/hide
    this.unsubscribers.push(
      gameEvents.on(GameEvents.HUD_SHOW_PROMPT, (data) => {
        this.showPrompt(data.text);
      })
    );

    this.unsubscribers.push(
      gameEvents.on(GameEvents.HUD_HIDE_PROMPT, () => {
        this.hidePrompt();
      })
    );

    // Toast from external systems
    this.unsubscribers.push(
      gameEvents.on(GameEvents.HUD_TOAST, (data) => {
        this.showToast(data.message, data.color);
      })
    );

    // Level complete — clear ALL transient UI so the completion toast
    // reads clean without stacking over stale prompts or prior toasts.
    this.unsubscribers.push(
      gameEvents.on(GameEvents.LEVEL_COMPLETE, () => {
        this.hidePuzzleProgress();
        this.hidePrompt();
        this.clearObjective();
        this.showToast("Level Complete!", MINNA_COLOR);
      })
    );

    // Always-on objective line — not gated by hintsEnabled.
    this.unsubscribers.push(
      gameEvents.on(GameEvents.HUD_SET_OBJECTIVE, (data) => {
        this.setObjective(data.text);
      })
    );

    // Making Biscuits — show/hide the kneading overlay and update paw
    // pads + trance-depth bar as beats fire.
    this.unsubscribers.push(
      gameEvents.on(GameEvents.BISCUITS_STARTED, (data) => {
        this.showBiscuitsOverlay(data.assistMode);
      })
    );
    this.unsubscribers.push(
      gameEvents.on(GameEvents.BISCUITS_BEAT, (data) => {
        this.biscuitsDepth = data.depth;
        this.biscuitsLastPaw = data.paw;
        this.biscuitsLastPawQuality = data.quality;
        this.biscuitsLastPawFlashAt = performance.now();
      })
    );
    this.unsubscribers.push(
      gameEvents.on(GameEvents.BISCUITS_COMPLETED, () => {
        // Snap the depth bar to full for one frame so the player sees the
        // meter top out before the vision fades in.
        this.biscuitsDepth = BISCUITS.MAX_DEPTH;
        this.updateBiscuitsOverlay();
        // Small delay before hiding so the fill "pops" briefly.
        setTimeout(() => this.hideBiscuitsOverlay(), 400);
      })
    );
    this.unsubscribers.push(
      gameEvents.on(GameEvents.BISCUITS_CANCELLED, () => {
        this.hideBiscuitsOverlay();
      })
    );

    // #15 Separation tether — light the edge strip whenever the camera
    // clamp damps a cat's outward motion. The event is throttled to
    // 10Hz in PlayerEntity; our update loop interpolates between
    // samples so the strip doesn't flicker.
    this.unsubscribers.push(
      gameEvents.on(GameEvents.PLAYER_SEPARATION_CLAMPED, (data) => {
        this.onSeparationClamped(data);
      })
    );

    // Listen to glyph change events to swap vector glyph formats
    this.unsubscribers.push(
      gameEvents.on(GameEvents.HUD_GLYPHS_CHANGED, (data) => {
        this.setControllerActive(data.controllerActive);
      })
    );

    // Listen to accessibility setting changes
    this.unsubscribers.push(
      gameEvents.on(GameEvents.ACCESSIBILITY_CHANGED, () => {
        this.applyAccessibilityTuning();
      })
    );
  }

  private applyAccessibilityTuning(): void {
    const a11y = AccessibilitySystem.getInstance();
    const scale = a11y.state.fontScale;
    const highContrast = a11y.state.highContrastUI;

    // Apply font scale dynamically to all instantiated elements
    if (this.biscuitsTitle) this.biscuitsTitle.fontSize = 18 * scale;
    if (this.biscuitsHint) this.biscuitsHint.fontSize = 12 * scale;
    if(this.biscuitsPanel&&this.biscuitsTitle&&this.biscuitsHint&&this.biscuitsDepthBg){
      const titleHeight=Math.max(22,Math.ceil(18*scale*1.25));
      this.biscuitsTitle.height=`${titleHeight}px`;
      const hintHeight=scale>1?Math.ceil(12*scale*2.5):18;
      // Size the enlarged panel from its content rows: title, 56px paws,
      // 12px depth bar and wrapped hint, with 12px between each row.
      // Centering the paws in an independently enlarged panel wastes the
      // space needed to keep both cats visible at 540p.
      this.biscuitsPanel.height=`${scale>1?titleHeight+hintHeight+128:160}px`;
      for(const pad of [this.biscuitsLeftPad,this.biscuitsRightPad])if(pad){
        pad.verticalAlignment=scale>1?Control.VERTICAL_ALIGNMENT_TOP:Control.VERTICAL_ALIGNMENT_CENTER;
        pad.top=scale>1?`${titleHeight+22}px`:"-8px";
      }
      this.biscuitsHint.height=`${hintHeight}px`;
      this.biscuitsHint.textWrapping=true;
      this.biscuitsHint.paddingLeft="12px";this.biscuitsHint.paddingRight="12px";
      this.biscuitsDepthBg.top=`-${hintHeight+26}px`;
    }
    if (this.characterLabel) this.characterLabel.fontSize = 18 * scale;
    if (this.harnessIcon) this.harnessIcon.fontSize = (this.harnessState === "intact" ? 11 : 13) * scale;
    if (this.puzzleLabel) this.puzzleLabel.fontSize = 17 * scale;
    if (this.promptLabel) this.promptLabel.fontSize = 15 * scale;

    // Scale energy labels
    if (this.minnaEnergyLabel) this.minnaEnergyLabel.fontSize = 12 * scale;
    if (this.basilEnergyLabel) this.basilEnergyLabel.fontSize = 12 * scale;

    // Scale ability slot labels
    for (const slot of this.abilitySlots.values()) {
      // Canopy scales the whole widget, including its text and feedback.
      slot.label.fontSize = this.avoidActors ? (this.atmospheric ? 11 : 9) : 9 * scale;
    }

    // Apply high contrast visuals to panels
    if (this.puzzlePanel) {
      this.puzzlePanel.background = highContrast ? "rgba(0, 0, 0, 0.98)" : this.panelBg;
      this.puzzlePanel.thickness = highContrast ? 3 : 1.5;
    }
    if (this.promptPanel) {
      this.promptPanel.background = highContrast ? "rgba(0, 0, 0, 0.98)" : this.panelBg;
      this.promptPanel.thickness = highContrast ? 3 : 1.5;
    }
    if (this.characterPanel) {
      this.characterPanel.background = highContrast ? "rgba(0, 0, 0, 0.98)" : this.panelBg;
      this.characterPanel.thickness = highContrast ? 3 : 1.5;
    }
    if (this.energyPanel) {
      this.energyPanel.background = highContrast ? "rgba(0, 0, 0, 0.98)" : this.panelBg;
      this.energyPanel.thickness = highContrast ? 3 : 1.5;
    }

    // Scale active toasts
    for (const toast of this.activeToasts) {
      const textBlock = toast.container.getChildByName("toastText") as TextBlock;
      if (textBlock) {
        textBlock.fontSize = 15 * scale;
      }
      if (highContrast) {
        toast.container.background = "rgba(0, 0, 0, 0.98)";
        toast.container.thickness = 3;
      } else {
        toast.container.background = this.panelBg;
        toast.container.thickness = 1.5;
      }
    }

    // Re-render active prompt if shown to apply scale instantly
    if (this.promptPanel && this.promptPanel.isVisible && this.lastPromptText) {
      this.showPrompt(this.lastPromptText);
    }

    // Re-tint long-lived character dot and harness fill colors.
    // The dot uses MINNA_AURA / BASIL_HARNESS keys; the harness bar uses
    // COPPER (intact) and BRASS (cracked). Both are long-lived GUI controls
    // (created once in buildCharacterIndicator / buildEnergyBars) so they
    // must be updated here rather than only at construction time.
    if (this.characterDot) {
      const minnaHex = a11y.resolveColor("MINNA_AURA", MINNA_COLOR);
      const basilHex = a11y.resolveColor("BASIL_HARNESS", BASIL_COLOR);
      this.characterDot.background = this.activePlayer === 1 ? minnaHex : basilHex;
    }
    if (this.harnessBarFill && this.harnessIcon) {
      // Only re-tint the intact/cracked states which use COPPER/BRASS;
      // venting/limping use fixed hue-shifted colors not in the a11y palette.
      if (this.harnessState === "intact") {
        const copperHex = a11y.resolveColor("COPPER", COLORS.COPPER);
        this.harnessBarFill.background = copperHex;
        this.harnessIcon.color = copperHex;
      } else if (this.harnessState === "cracked") {
        const brassHex = a11y.resolveColor("BRASS", COLORS.BRASS);
        this.harnessBarFill.background = brassHex;
        this.harnessIcon.color = brassHex;
      }
    }
  }

  // ===================================================================
  // UPDATE LOOP
  // ===================================================================

  private controlRect(panel: Control|null): ScreenRect|null {
    if(!panel?.isVisible)return null;
    const size=this.guiTexture.getSize(),engine=this.scene.getEngine();
    const sx=engine.getRenderWidth()/size.width,sy=engine.getRenderHeight()/size.height;
    return{left:(panel.centerX-panel.widthInPixels/2)*sx,top:(panel.centerY-panel.heightInPixels/2)*sy,
      width:panel.widthInPixels*sx,height:panel.heightInPixels*sy};
  }

  private fixedHudRects(except?:Control|null):ScreenRect[]{
    return[this.characterPanel,this.energyPanel,this.abilitiesPanel,this.promptPanel,
      this.activeToasts.length?this.toastContainer:null].filter(panel=>panel!==except)
      .map(panel=>panel===this.abilitiesPanel&&this.abilityRailRect?this.abilityRailRect:this.controlRect(panel))
      .filter((rect):rect is ScreenRect=>rect!==null);
  }

  private projectMeshes(meshes:readonly AbstractMesh[]):ScreenRect|null {
    const camera=this.scene.activeCamera;if(!camera||!meshes.length)return null;
    const engine=this.scene.getEngine(),viewport=camera.viewport.toGlobal(engine.getRenderWidth(),engine.getRenderHeight());
    const transform=camera.getViewMatrix(true).multiply(camera.getProjectionMatrix(true));
    const points=meshes.flatMap(mesh=>{mesh.computeWorldMatrix(true);return mesh.getBoundingInfo().boundingBox.vectorsWorld
      .map(point=>Vector3.Project(point,Matrix.IdentityReadOnly,transform,viewport));});
    const left=Math.min(...points.map(point=>point.x)),top=Math.min(...points.map(point=>point.y));
    return{left,top,width:Math.max(...points.map(point=>point.x))-left,height:Math.max(...points.map(point=>point.y))-top};
  }

  private positionPanel(panel:Control,rect:ScreenRect):void {
    const toDesign=(this.guiTexture.idealWidth||this.guiTexture.getSize().width)/this.scene.getEngine().getRenderWidth();
    panel.horizontalAlignment=Control.HORIZONTAL_ALIGNMENT_LEFT;panel.verticalAlignment=Control.VERTICAL_ALIGNMENT_TOP;
    panel.left=`${rect.left*toDesign}px`;panel.top=`${rect.top*toDesign}px`;
  }

  /** Local Canopy composition: keep actual actors visible even when a full
   * leap cannot share the ordinary header band under the fixed zoom ceiling. */
  private updateActorClearance():void {
    if(!this.avoidActors)return;
    this.abilityRailRect=null;
    this.cutsceneActive=this.cinematicActive?.()??this.cutsceneActive;
    this.guiTexture.rootContainer.isVisible=!this.cutsceneActive;
    if(this.puzzlePanel)this.puzzlePanel.isVisible=this.puzzleVisible&&!this.biscuitsActive;
    if(this.objectivePanel)this.objectivePanel.isVisible=!!this.objectiveLabel?.text&&!this.biscuitsActive;
    this.refreshCueVisibility();
    if(this.cutsceneActive){this.headerRect=null;return;}
    const engine=this.scene.getEngine(),w=engine.getRenderWidth(),h=engine.getRenderHeight();
    const actors=["minna","basil"].map(name=>this.scene.metadata?.[name] as {mesh?:AbstractMesh;grounded?:boolean}|undefined);
    this.actorRects=actors.map(actor=>{
      const root=actor?.mesh;if(!root?.isEnabled())return null;
      const meshes=root.getChildMeshes().filter(mesh=>mesh.skeleton&&mesh.isEnabled()&&mesh.isVisible&&mesh.getTotalVertices()>0);
      if(!meshes.length&&root.isVisible&&root.getTotalVertices()>0)meshes.push(root);
      return this.projectMeshes(meshes);
    }).filter((rect):rect is ScreenRect=>rect!==null);
    const viewport={width:w,height:h},designScale=w/(this.guiTexture.idealWidth||w);
    const rail=this.controlRect(this.abilitiesPanel);
    if(this.abilitiesPanel&&rail&&rail.width>0&&rail.height>0){
      const count=Math.max(this.abilitySlots.size,1);
      const widgetScale=AccessibilitySystem.getInstance().state.fontScale;
      const slotSize=ABILITY_SLOT_SIZE*widgetScale;
      for(const {layout,bg} of this.abilitySlots.values()){
        layout.width=`${slotSize}px`;layout.height=`${slotSize}px`;
        bg.scaleX=widgetScale;bg.scaleY=widgetScale;
      }
      const preferred={left:16*designScale,
        top:(this.energyEnabled?ABILITY_RAIL_TOP_WITH_ENERGY:ABILITY_RAIL_TOP_WITH_HARNESS_CHIP)*designScale,
        width:(slotSize+8)*designScale,height:(count*slotSize+12)*designScale};
      const otherHud=this.fixedHudRects(this.abilitiesPanel);
      const obstacles=[...this.actorRects,...otherHud];
      const separate=(r:ScreenRect,o:ScreenRect,margin:number)=>r.left>=o.left+o.width+margin||
        r.left+r.width<=o.left-margin||r.top>=o.top+o.height+margin||r.top+r.height<=o.top-margin;
      const clear=(r:ScreenRect,margin:number)=>r.left>=0&&r.top>=0&&r.left+r.width<=w&&r.top+r.height<=h&&
        this.actorRects.every(o=>separate(r,o,margin))&&otherHud.every(o=>separate(r,o,0));
      // Use a compact dock when the edge column would cover a cat. Keep a
      // clear dock steady until the usual column has extra actor clearance.
      let horizontal=false,placed:ScreenRect|null=preferred;
      if(!clear(preferred,24)){
        const width=(count*slotSize+12)*designScale,height=(slotSize+8)*designScale;
        const dock={left:(w-width)/2,top:h-height-16*designScale,width,height};
        horizontal=true;
        placed=!this.abilitiesPanel.isVertical&&Math.abs(rail.width-width)<1&&
          Math.abs(rail.height-height)<1&&clear(rail,12)?rail:placePanelAvoidingRects(dock,viewport,obstacles);
        if(!placed){horizontal=false;placed=placePanelAvoidingRects(preferred,viewport,obstacles);}
      }
      if(placed){
        if(this.abilitiesPanel.isVertical===horizontal){
          // StackPanel retains offsets on its former stacking axis. Reset
          // them so every existing slot survives both orientation changes.
          for(const {layout} of this.abilitySlots.values()){
            layout.left="0px";layout.top="0px";
            layout.horizontalAlignment=Control.HORIZONTAL_ALIGNMENT_CENTER;
            layout.verticalAlignment=Control.VERTICAL_ALIGNMENT_CENTER;
          }
        }
        this.abilitiesPanel.isVertical=!horizontal;
        this.abilitiesPanel.width=`${horizontal?count*slotSize+12:slotSize+8}px`;
        this.abilitiesPanel.height=`${horizontal?slotSize+8:count*slotSize+12}px`;
        this.positionPanel(this.abilitiesPanel,placed);
        this.abilityRailRect=placed;
      }
    }
    const fixed=this.fixedHudRects();
    if(this.biscuitsActive){
      this.headerRect=null;
      if(this.biscuitsPanel){
        const texture=this.guiTexture.getSize(),width=this.biscuitsPanel.widthInPixels*w/texture.width,height=this.biscuitsPanel.heightInPixels*h/texture.height;
        const sweater=this.projectMeshes(this.scene.meshes.filter(mesh=>mesh.isEnabled()&&mesh.isVisible&&mesh.getTotalVertices()>0&&
          /^(CanopyParlor_sweater|canopyParlorFallback_sweater(Fold)?)$/.test(mesh.name.replace(/^(Clone of )+/,""))));
        const placed=placePanelAvoidingRects({left:(w-width)/2,top:(h-height)/2,width,height},viewport,
          [...this.actorRects,...fixed,...(sweater?[sweater]:[])]);
        if(placed)this.positionPanel(this.biscuitsPanel,placed);
      }
      return;
    }
    if(this.puzzlePanel?.isVisible||this.objectivePanel?.isVisible){
      const texture=this.guiTexture.getSize();
      const width=Math.max(this.puzzlePanel?.widthInPixels??0,this.objectivePanel?.widthInPixels??0)*w/texture.width;
      const preferred={left:(w-width)/2,top:16*designScale,width,height:76*designScale};
      const airborne=actors.some(actor=>actor?.grounded===false);
      const from=airborne&&this.headerRect?{...this.headerRect,width,height:preferred.height}:preferred;
      const placed=placePanelAvoidingRects(from,viewport,[...this.actorRects,...fixed])??preferred;
      this.headerRect=placed;
      if(this.puzzlePanel)this.positionPanel(this.puzzlePanel,{...placed,left:placed.left+(width-this.puzzlePanel.widthInPixels*w/texture.width)/2});
      if(this.objectivePanel)this.positionPanel(this.objectivePanel,{...placed,top:placed.top+48*designScale,left:placed.left+(width-this.objectivePanel.widthInPixels*w/texture.width)/2});
    }else this.headerRect=null;
  }

  private updateLoopObserver: ReturnType<typeof setInterval> | null = null;

  private startUpdateLoop(): void {
    // Use scene's onBeforeRenderObservable for frame-synced updates
    this.scene.onBeforeRenderObservable.add(() => {
      this.updateAbilitySlots();
      this.updateEnergyBars();
      this.updateHarnessBar();
      this.updateToasts();
      this.updateActorClearance();
      this.updateCues();
      this.updateBiscuitsOverlay();
      this.updateSeparationTether();
    });
  }

  /**
   * Draw the harness-integrity bar. Copper while Intact (30% alpha,
   * quiet background), escalating color + full alpha once damaged.
   * A brief heal-pulse tint plays when HARNESS_HEALED fires.
   */
  private updateHarnessBar(): void {
    if (!this.harnessBarBg || !this.harnessBarFill || !this.harnessIcon) return;

    const pct = Math.max(0, Math.min(1, this.harnessFraction));
    this.harnessBarFill.width = `${Math.round(pct * 100)}%`;

    // State-driven color + alpha. Intact reads as a quiet accent,
    // damaged states pulse in copper/amber/red per spec §5.
    let color: string = COLORS.COPPER;
    let baseAlpha = 0.3;
    let glyph = "\u2699"; // cog
    switch (this.harnessState) {
      case "intact":
        color = COLORS.COPPER;
        baseAlpha = 0.3;
        glyph = "\u2699"; // cog
        break;
      case "cracked":
        color = COLORS.BRASS;
        baseAlpha = 0.7;
        glyph = "\u26E0"; // restricted (hairline)
        break;
      case "venting":
        color = "#d98a3a"; // amber
        baseAlpha = 1;
        glyph = "\u26A0"; // warning
        break;
      case "limping":
        color = "#cc4422"; // deep red
        baseAlpha = 1;
        glyph = "\u2620"; // skull
        break;
    }
    this.harnessBarFill.background = color;
    this.harnessIcon.text = glyph;
    this.harnessIcon.color = color;

    // Heal-pulse: fade a cyan-ish accent over the bar for 0.5s.
    if (this.harnessHealPulseTimer > 0) {
      this.harnessHealPulseTimer = Math.max(0, this.harnessHealPulseTimer - 0.016);
      const t = this.harnessHealPulseTimer / 0.5;
      this.harnessBarBg.alpha = baseAlpha + t * 0.6;
    } else {
      // Heartbeat pulse while Venting/Limping per spec §5.
      if (this.harnessState === "venting" || this.harnessState === "limping") {
        const bpm = this.harnessState === "limping" ? 80 : 60;
        const phase = Math.sin(performance.now() * (bpm / 60) * Math.PI * 2 * 0.001);
        this.harnessBarBg.alpha = baseAlpha * (0.75 + phase * 0.15);
      } else {
        this.harnessBarBg.alpha = baseAlpha;
      }
    }
  }

  /**
   * Update solo/co-op mode state.
   */
  setSoloMode(solo: boolean): void {
    this.soloMode = solo;
    this.updateCharacterIndicator();
    this.renderCues();
  }

  // ===================================================================
  // UTILITIES
  // ===================================================================

  private animateAlpha(
    control: Rectangle | Ellipse,
    from: number,
    to: number,
    durationMs: number,
    onComplete?: () => void,
  ): void {
    const start = performance.now();
    const tick = () => {
      const elapsed = performance.now() - start;
      const t = Math.min(elapsed / durationMs, 1);
      control.alpha = from + (to - from) * t;
      if (t < 1) {
        requestAnimationFrame(tick);
      } else if (onComplete) {
        onComplete();
      }
    };
    requestAnimationFrame(tick);
  }

  // ===================================================================
  // LIFECYCLE
  // ===================================================================

  /** Dispose all GUI resources and unsubscribe from events. */
  dispose(): void {
    this.disposeGuiScaling?.();
    this.disposeGuiScaling = null;
    this.cueProxy?.dispose();
    this.cueProxy = null;
    for (const unsub of this.unsubscribers) {
      unsub();
    }
    this.unsubscribers = [];
    if (this.hintsKeyHandler) {
      if (typeof window !== "undefined" && typeof window.removeEventListener === "function") {
        window.removeEventListener("keydown", this.hintsKeyHandler);
      }
      this.hintsKeyHandler = null;
    }

    if (this.guiTexture) {
      this.guiTexture.dispose();
    }

    this.abilitySlots.clear();
    this.abilityCooldowns.clear();
    this.activeToasts = [];
  }
}
